/** Mask weak NWS-style reflectivity colors (clear-air / clutter speckle on PNG tiles). */
export function shouldMaskWeakReflectivityPixel(
  r: number,
  g: number,
  b: number,
  a: number
): boolean {
  if (a < 12) return false;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const sat = max - min;
  if (max < 20) return true;
  if (max < 72 && sat <= 38) return true;
  if (max < 118 && sat < 32 && g >= r - 8 && b >= r - 12) return true;
  return false;
}

/** Slight boost on pixels we keep so precip reads on the basemap without looking neon. */
function boostReflectivityPixel(r: number, g: number, b: number, a: number): [number, number, number, number] {
  if (a < 12) return [r, g, b, a];
  const gain = 1.05;
  return [
    Math.min(255, Math.round(r * gain)),
    Math.min(255, Math.round(g * gain)),
    Math.min(255, Math.round(b * gain)),
    a,
  ];
}

/** Return a data URL with sub-threshold speckle pixels made transparent. */
export function filterRadarTileImage(img: HTMLImageElement): string | null {
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  if (!w || !h) return img.src;

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return img.src;

  ctx.drawImage(img, 0, 0);
  const imageData = ctx.getImageData(0, 0, w, h);
  const d = imageData.data;
  for (let i = 0; i < d.length; i += 4) {
    const r = d[i]!;
    const g = d[i + 1]!;
    const b = d[i + 2]!;
    const a = d[i + 3]!;
    if (shouldMaskWeakReflectivityPixel(r, g, b, a)) {
      d[i + 3] = 0;
      continue;
    }
    const [nr, ng, nb, na] = boostReflectivityPixel(r, g, b, a);
    d[i] = nr;
    d[i + 1] = ng;
    d[i + 2] = nb;
    d[i + 3] = na;
  }
  let visible = 0;
  for (let i = 3; i < d.length; i += 4) {
    if (d[i]! > 12) visible++;
  }
  if (visible === 0) return null;

  ctx.putImageData(imageData, 0, 0);
  return canvas.toDataURL('image/png');
}
